import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RequestQueueCard = ({ request, onStatusUpdate, onViewDetails }) => {
  const getUrgencyConfig = (urgency) => {
    return urgency === 'Emergency'
      ? { color: 'bg-destructive text-destructive-foreground border-destructive', icon: 'AlertTriangle', label: 'CRITICAL' }
      : { color: 'bg-warning text-warning-foreground border-warning', icon: 'Clock', label: 'SCHEDULED' };
  };

  const getStatusConfig = (status) => {
    const configs = {
      'pending': { color: 'bg-warning/10 text-warning border-warning/20', icon: 'Clock', label: 'Pending' },
      'in-progress': { color: 'bg-primary/10 text-primary border-primary/20', icon: 'Activity', label: 'In Progress' },
      'completed': { color: 'bg-success/10 text-success border-success/20', icon: 'CheckCircle2', label: 'Completed' },
      'cancelled': { color: 'bg-muted text-muted-foreground border-border', icon: 'XCircle', label: 'Cancelled' }
    };
    return configs?.[status] || configs?.['pending'];
  };

  const getIssueTypeIcon = (issueType) => {
    const icons = {
      'Mechanical': 'Wrench',
      'Electrical': 'Zap',
      'Safety': 'Shield',
      'Routine Maintenance': 'Settings',
      'Other': 'HelpCircle'
    };
    return icons?.[issueType] || 'HelpCircle';
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const urgencyConfig = getUrgencyConfig(request?.urgency);
  const statusConfig = getStatusConfig(request?.status);

  const quickActions = [
    { label: 'Acknowledge', status: 'in-progress', icon: 'Check', variant: 'default' },
    { label: 'Complete', status: 'completed', icon: 'CheckCircle2', variant: 'success' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1 overflow-hidden hover:shadow-elevation-2 transition-smooth">
      {/* Urgency Banner */}
      {request?.urgency === 'Emergency' && (
        <div className={`${urgencyConfig?.color} px-4 py-2 flex items-center justify-center gap-2`}>
          <Icon name={urgencyConfig?.icon} size={16} strokeWidth={2} />
          <span className="text-xs font-bold uppercase tracking-wide">{urgencyConfig?.label}</span>
        </div>
      )}

      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex items-start gap-3 flex-1">
            <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg flex-shrink-0">
              <Icon name={getIssueTypeIcon(request?.issue_type)} size={24} color="var(--color-primary)" strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-foreground truncate">{request?.request_number}</h3>
              <p className="text-sm text-muted-foreground">{request?.issue_type}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${statusConfig?.color}`}>
                  <Icon name={statusConfig?.icon} size={12} strokeWidth={2} />
                  {statusConfig?.label}
                </span>
                {request?.urgency === 'Scheduled' && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${urgencyConfig?.color}`}>
                    <Icon name={urgencyConfig?.icon} size={12} strokeWidth={2} />
                    {urgencyConfig?.label}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-xs text-muted-foreground">{formatTime(request?.created_at)}</p>
          </div>
        </div>

        {/* Driver & Unit Info */}
        <div className="grid grid-cols-2 gap-3 mb-4 bg-muted/30 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <Icon name="User" size={16} color="var(--color-primary)" strokeWidth={2} />
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">Driver</p>
              <p className="text-sm font-medium text-foreground truncate">{request?.driver_phone}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Truck" size={16} color="var(--color-primary)" strokeWidth={2} />
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">Unit</p>
              <p className="text-sm font-medium text-foreground truncate">{request?.unit_number}</p>
            </div>
          </div>
        </div>

        {/* Location */}
        {request?.gps_address && (
          <div className="flex items-start gap-2 mb-4">
            <Icon name="MapPin" size={16} color="var(--color-primary)" strokeWidth={2} className="flex-shrink-0 mt-0.5" />
            <p className="text-sm text-foreground">{request?.gps_address}</p>
          </div>
        )}

        {/* Description Preview */}
        <div className="mb-4">
          <p className="text-sm text-muted-foreground line-clamp-2">{request?.description}</p>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="default"
            size="sm"
            iconName="Eye"
            iconPosition="left"
            onClick={() => onViewDetails(request?.id)}
            className="flex-1"
          >
            View Details
          </Button>
          {request?.status === 'pending' && (
            <Button
              variant="outline"
              size="sm"
              iconName="Check"
              iconPosition="left"
              onClick={() => onStatusUpdate(request?.id, 'in-progress')}
            >
              Acknowledge
            </Button>
          )}
          {request?.status === 'in-progress' && (
            <Button
              variant="success"
              size="sm"
              iconName="CheckCircle2"
              iconPosition="left"
              onClick={() => onStatusUpdate(request?.id, 'completed')}
            >
              Complete
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestQueueCard;